import React from "react";

import {
  Calendar,
  CheckCircle,
  GraduationCap,
  MapPin,
  Quote,
  Target,
  TrendingUp,
  Users,
} from "lucide-react";
const highlightCards = [
  {
    title: "Academic Excellence",
    description:
      "Strong curriculum with modern teaching methods and continuous evaluation.",
    icon: GraduationCap,
    color: "red",
    points: [
      "CBSE affiliated curriculum",
      "Experienced faculty members",
      "Smart class technology",
    ],
  },
  {
    title: "Holistic Development",
    description:
      "Balanced growth through academics, sports, arts and personality development.",
    icon: Users,
    color: "yellow",
    points: [
      "Sports and physical education",
      "Cultural and arts programs",
      "Personality development",
    ],
  },
  {
    title: "Modern Infrastructure",
    description:
      "Advanced facilities creating a secure and engaging learning environment.",
    icon: Target,
    color: "blue",
    points: [
      "Well-equipped laboratories",
      "Digital library and resources",
      "Safe and secure campus",
    ],
  },
];

const infoCards = [
  {
    title: "Academic Calendar",
    description:
      "Well-planned schedule for balanced learning.",
    icon: Calendar,
    color: "red",
  },
  {
    title: "Continuous Progress",
    description:
      "Regular assessment and feedback system.",
    icon: TrendingUp,
    color: "yellow",
  },
  {
    title: "Prime Location",
    description:
      "Easily accessible campus in Purvanchal.",
    icon: MapPin,
    color: "blue",
  },
];

const colorClasses = {
  red: {
    line: "from-red-800 to-red-500",
    iconBox:
      "bg-red-50 text-red-700 group-hover:bg-red-700 group-hover:text-white",
    check: "text-red-700",
    border: "hover:border-red-200",
  },

  yellow: {
    line: "from-yellow-500 to-orange-500",
    iconBox:
      "bg-yellow-50 text-yellow-700 group-hover:bg-yellow-500 group-hover:text-white",
    check: "text-yellow-600",
    border: "hover:border-yellow-300",
  },

  blue: {
    line: "from-[#1B2A4A] to-red-800",
    iconBox:
      "bg-slate-100 text-[#1B2A4A] group-hover:bg-[#1B2A4A] group-hover:text-white",
    check: "text-[#1B2A4A]",
    border: "hover:border-slate-300",
  },
};

const SchoolHighlights = () => {
  return (
    <section className="relative w-full overflow-hidden bg-gradient-to-br from-[#f4f7fa] via-white to-red-50 py-8 sm:py-10 lg:py-12">
      {/* Background decoration */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-28 top-10 h-64 w-64 rounded-full bg-red-300/15 blur-3xl" />

        <div className="absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-yellow-300/20 blur-3xl" />
      </div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-10 xl:px-16">
        {/* Heading */}
        <div className="mb-7 text-center">

          <h2 className="mt-3 font-serif text-3xl font-black text-[#1B2A4A] sm:text-4xl lg:text-[44px]">
            Our{" "}
            <span className="relative inline-block text-red-700">
              School at a Glance

              <span className="absolute -bottom-1 left-0 h-1 w-full rounded-full bg-gradient-to-r from-red-700 to-yellow-500" />
            </span>
          </h2>

          <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-slate-500 sm:text-[15px]">
            Excellence in education, holistic development,
            modern infrastructure and strong character
            building.
          </p>
        </div>

        {/* Main cards */}
        <div className="grid gap-4 lg:grid-cols-3">
          {highlightCards.map((item) => {
            const Icon = item.icon;
            const styles = colorClasses[item.color];

            return (
              <article
                key={item.title}
                className={`group overflow-hidden rounded-[20px] border border-slate-100 bg-white shadow-[0_12px_35px_rgba(15,23,42,0.07)] transition duration-500 hover:-translate-y-1.5 hover:shadow-[0_22px_50px_rgba(15,23,42,0.13)] ${styles.border}`}
              >
                <div
                  className={`h-1.5 bg-gradient-to-r ${styles.line}`}
                />

                <div className="p-5">
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-xl transition duration-300 group-hover:scale-105 ${styles.iconBox}`}
                  >
                    <Icon size={24} />
                  </div>

                  <h3 className="mt-4 font-serif text-xl font-black text-[#1B2A4A]">
                    {item.title}
                  </h3>

                  <p className="mt-2 min-h-[48px] text-sm leading-6 text-slate-600">
                    {item.description}
                  </p>

                  <ul className="mt-4 space-y-2">
                    {item.points.map((point) => (
                      <li
                        key={point}
                        className="flex items-start gap-2 text-sm text-slate-600"
                      >
                        <CheckCircle
                          size={15}
                          className={`mt-0.5 shrink-0 ${styles.check}`}
                        />

                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>

        {/* Quote and admission CTA */}
        <div className="relative mt-6 overflow-hidden rounded-[20px] bg-gradient-to-r from-red-800 via-red-700 to-red-600 px-5 py-5 shadow-lg sm:px-6">
          <div className="pointer-events-none absolute -right-12 -top-16 h-48 w-48 rounded-full bg-yellow-300/15 blur-2xl" />

          <div className="relative flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/15 text-yellow-400">
                <Quote size={23} />
              </div>

              <div>
                <p className="max-w-3xl text-sm italic leading-6 text-white/90 sm:text-[15px]">
                  “We are committed to nurturing scholars
                  who will become responsible citizens and
                  future leaders of our nation.”
                </p>

                <p className="mt-2 text-xs font-black uppercase tracking-[0.12em] text-yellow-400">
                  — St. Xavier&apos;s Public School
                </p>
              </div>
            </div>

            <a
              href="/admission"
              className="inline-flex shrink-0 items-center justify-center rounded-full bg-white px-5 py-2.5 text-sm font-black text-red-700 shadow-md transition duration-300 hover:-translate-y-0.5 hover:bg-yellow-400 hover:text-red-900"
            >
              Admission Open 2026–27
            </a>
          </div>
        </div>
        {/* Bottom information cards */}
        <div className="mt-5 grid gap-3 md:grid-cols-3">
          {infoCards.map((item) => {
            const Icon = item.icon;
            const styles = colorClasses[item.color];

            return (
              <div
                key={item.title}
                className="group rounded-xl border border-slate-100 bg-white p-4 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition duration-300 ${styles.iconBox}`}
                  >
                    <Icon size={19} />
                  </div>

                  <div>
                    <h4 className="text-sm font-black text-[#1B2A4A] sm:text-base">
                      {item.title}
                    </h4>

                    <p className="mt-1 text-xs leading-5 text-slate-500 sm:text-sm">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SchoolHighlights;